"use client";

import React, { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import Link from "next/link";
import "./managed-services.css";

/* Images — public/assets/images/ se serve hoti hain */
const IMG = "/assets/images";
const img = (name: string) => encodeURI(`${IMG}/${name}`);
const imgHero = img("managed-services-hero.jpg");
const imgProactive = img("End-to-End-Visibility.jpg");
const imgStory = img("Retail-&-CPG1.jpg");
const imgCta = img("lets-banner.jpg");

/* All styling lives in managed-services.css (no styling in this file). */

/* ---------- Scroll reveal (bottom to top) ---------- */

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll<HTMLElement>("[data-reveal]");

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );

    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return ref;
}

const delay = (i: number, step = 80, max = 400) =>
  ({ "--ms-delay": `${Math.min(i * step, max)}ms` } as React.CSSProperties);

/* ---------- Icons ---------- */

type IconKey =
  | "monitor" | "db" | "server" | "shield" | "cloud" | "eye" | "alert" | "wrench"
  | "chart" | "factory" | "cart" | "bank" | "bolt" | "heart";

function Icon({ name }: { name: IconKey }) {
  const paths: Record<IconKey, ReactNode> = {
    monitor: (<><rect x="3" y="4" width="18" height="12" rx="1.5" /><path d="M8 20h8M12 16v4" /></>),
    db: (<><ellipse cx="12" cy="5.5" rx="7" ry="2.8" /><path d="M5 5.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6M5 11.5v6c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-6" /></>),
    server: (<><rect x="4" y="4" width="16" height="6" rx="1" /><rect x="4" y="14" width="16" height="6" rx="1" /><path d="M8 7h.01M8 17h.01" /></>),
    shield: (<><path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.1 7.5 9.5 4.4-1.4 7.5-4.9 7.5-9.5V6L12 3Z" /><path d="m9 12 2.2 2.2L15.5 10" /></>),
    cloud: <path d="M7 18a4.5 4.5 0 0 1-.6-8.96A6 6 0 0 1 18 9.5a4.2 4.2 0 0 1-.5 8.5H7Z" />,
    eye: (<><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="2.8" /></>),
    alert: (<><path d="M12 4 3 19h18L12 4Z" /><path d="M12 10v4M12 17h.01" /></>),
    wrench: <path d="M14.5 6.5a4 4 0 0 0-5 5L4 17l3 3 5.5-5.5a4 4 0 0 0 5-5l-2.5 2.5-2.5-.5-.5-2.5 2.5-2.5Z" />,
    chart: <path d="M5 20V12M11 20V4M17 20V9M3 20h18" />,
    factory: <path d="M3 20V9l6 4V9l6 4V6h3v14H3ZM7 20v-3M11 20v-3M15 20v-3" />,
    cart: (<><circle cx="9" cy="19" r="1.6" /><circle cx="17" cy="19" r="1.6" /><path d="M3 4h2.5l2 11h10l2-8H7" /></>),
    bank: <path d="M3 9.5 12 4l9 5.5M5 10v8M9.5 10v8M14.5 10v8M19 10v8M3 20h18" />,
    bolt: <path d="M13 3 5 13.5h6L10 21l8-10.5h-6L13 3Z" />,
    heart: <path d="M12 20s-7.5-4.6-7.5-10A4.3 4.3 0 0 1 12 7.5 4.3 4.3 0 0 1 19.5 10c0 5.4-7.5 10-7.5 10Z" />,
  };

  return (
    <svg
      className="ms-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

/* Partner logo: agar file na mile to naam text me dikhta hai */
function PartnerLogo({ name, file }: { name: string; file: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <span className="ms-partner-text">{name}</span>;
  return <img src={img(file)} alt={name} loading="lazy" onError={() => setFailed(true)} />;
}

/* ============================== DATA ============================== */

const partners = [
  { name: "SAP", file: "sap (1).jpg" },
  { name: "Salesforce", file: "salesforce.jpg" },
  { name: "Microsoft", file: "microsoft.jpg" },
  { name: "AWS", file: "aws.svg" },
  { name: "Google Cloud", file: "google-cloud.svg" },
];

const scope: { icon: IconKey; title: string }[] = [
  { icon: "monitor", title: "Applications" },
  { icon: "db", title: "Databases" },
  { icon: "server", title: "Infrastructure" },
  { icon: "shield", title: "Security" },
  { icon: "cloud", title: "Cloud" },
];

const outcomes = [
  { big: "40%", text: "Faster issue resolution" },
  { big: "99.9%", text: "System availability" },
  { big: "30%", text: "Lower operational costs" },
];

const proactive: { icon: IconKey; top: string; bottom: string }[] = [
  { icon: "eye", top: "Monitor", bottom: "24/7" },
  { icon: "alert", top: "Detect", bottom: "Issues Early" },
  { icon: "wrench", top: "Resolve", bottom: "Faster" },
  { icon: "chart", top: "Optimise", bottom: "Continuously" },
];

const approach = [
  { title: "Discover", text: "Understand environment, goals and challenges." },
  { title: "Manage", text: "Proactive monitoring, incident and problem management." },
  { title: "Optimise", text: "Continuous improvement with automation and insights." },
  { title: "Evolve", text: "Scale and adapt to meet changing business needs." },
];

const storyStats = [
  { big: "99.9%", text: "Availability achieved" },
  { big: "30%", text: "Reduction in operational costs" },
];

const otherCases: { icon: IconKey; title: string }[] = [
  { icon: "monitor", title: "Application support" },
  { icon: "server", title: "Infrastructure management" },
  { icon: "cloud", title: "Cloud operations" },
  { icon: "shield", title: "Security operations" },
  { icon: "db", title: "Database administration" },
];

const industries: { icon: IconKey; title: string }[] = [
  { icon: "factory", title: "Manufacturing" },
  { icon: "cart", title: "Retail & Consumer" },
  { icon: "bank", title: "Banking & Financial Services" },
  { icon: "bolt", title: "Energy & Utilities" },
  { icon: "heart", title: "Life Sciences & Healthcare" },
];

/* ============================== PAGE ============================== */

export default function ManagedServicesPage() {
  const ref = useScrollReveal();

  return (
    <div className="ms-page" ref={ref}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="ms-hero" id="top">
          <div className="ms-hero-bg" style={{ backgroundImage: `url("${imgHero}")` }} aria-hidden="true"></div>
          <div className="ms-hero-overlay" aria-hidden="true"></div>

          <div className="ms-hero-inner">
            <div className="ms-hero-copy">
              <h1 data-reveal style={delay(0)}>
                <span className="ms-line">Keep your business</span>
                <span className="ms-line"><em>running at</em></span>
                <span className="ms-line"><em>its best.</em></span>
              </h1>
              <p data-reveal style={delay(1, 120)}>
                Orane&apos;s managed services keep your critical systems secure, available and high performing —
                so your teams can focus on what matters most.
              </p>
              <div className="ms-actions" data-reveal style={delay(2, 120)}>
                <Link href="/tellusyourproblem#tell-details" className="ms-btn ms-btn-primary">
                  Talk to Our Experts <span aria-hidden="true">→</span>
                </Link>
                <Link href="#services" className="ms-btn ms-btn-outline">
                  Explore Our Services <span aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ============================= PARTNERS ============================ */}
        <section className="ms-partners" aria-label="Technology partners">
          <div className="ms-container">
            <div className="ms-partners-row" data-reveal>
              <p>Trusted to manage mission-critical systems across industries.</p>
              <ul>
                {partners.map((p) => (
                  <li key={p.name}><PartnerLogo name={p.name} file={p.file} /></li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ===================== END-TO-END (scope tiles) ==================== */}
        <section className="ms-section" id="services">
          <div className="ms-container">
            <div className="ms-e2e">
              <div className="ms-e2e-copy" data-reveal>
                <h2>End-to-end managed services for a resilient enterprise.</h2>
                <p>
                  We manage your applications, infrastructure and cloud environments with proactive monitoring,
                  automation and domain expertise to ensure stability, security and continuous improvement.
                </p>
                {/* <Link href="/solutions/managed-services/overview" className="ms-link ms-link--lg">
                  Explore our managed services <span aria-hidden="true">→</span>
                </Link> */}
              </div>

              <div className="ms-orbit" data-reveal style={delay(1, 100)}>
                <span className="ms-orbit-ring" aria-hidden="true"></span>
                <div className="ms-orbit-core">
                  <Icon name="cloud" />
                  <strong>Managed Services</strong>
                </div>
                <ul>
                  {scope.map((s) => (
                    <li key={s.title}>
                      <Icon name={s.icon} />
                      <span>{s.title}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ============================= OUTCOMES ============================ */}
        <section className="ms-section ms-section--tight" aria-label="Business outcomes">
          <div className="ms-container">
            <div className="ms-outcomes" data-reveal>
              <h2>Business outcomes</h2>
              <ul>
                {outcomes.map((o) => (
                  <li key={o.text}>
                    <strong>{o.big}</strong>
                    <span>{o.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ============================== PROACTIVE ========================== */}
        <section className="ms-proactive-section">
          <div className="ms-container">
            <div className="ms-proactive">
              <div className="ms-proactive-copy" data-reveal>
                <h2>Proactive support. Real business impact.</h2>
                <p>
                  From monitoring to continuous optimisation, we keep your systems reliable, secure and ready for
                  what&apos;s next.
                </p>
                <ul className="ms-proactive-list">
                  {proactive.map((p) => (
                    <li key={p.top}>
                      <span className="ms-proactive-icon"><Icon name={p.icon} /></span>
                      <span>
                        <strong>{p.top}</strong> {p.bottom}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="ms-proactive-image" data-reveal style={delay(1, 100)}>
                <img src={imgProactive} alt="" aria-hidden="true" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* ============================ APPROACH ============================= */}
        <section className="ms-section">
          <div className="ms-container">
            <div className="ms-head" data-reveal>
              <h2>Our approach</h2>
              {/* <Link href="/how-we-work" className="ms-link">See how it works <span aria-hidden="true">→</span></Link> */}
            </div>

            <ol className="ms-approach">
              {approach.map((a, i) => (
                <li className="ms-approach-item" key={a.title} data-reveal style={delay(i, 90)}>
                  <span className="ms-approach-no">{String(i + 1).padStart(2, "0")}</span>
                  <h3>{a.title}</h3>
                  <p>{a.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ============================ CUSTOMER STORY ======================= */}
        <section className="ms-story-section">
          <div className="ms-container">
            <div className="ms-story">
              <div className="ms-story-copy" data-reveal>
                <h2>Improved system availability for a global retail enterprise.</h2>
                <p>
                  Orane&apos;s managed services helped a leading retailer achieve 99.9% system availability and 30%
                  lower operational costs.
                </p>
                {/* <Link href="/customers/managed-services" className="ms-link">
                  Read the full story <span aria-hidden="true">→</span>
                </Link> */}
                <ul className="ms-story-stats">
                  {storyStats.map((s) => (
                    <li key={s.text}>
                      <strong>{s.big}</strong>
                      <span>{s.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="ms-story-image" data-reveal style={delay(1, 100)}>
                <img src={imgStory} alt="" aria-hidden="true" loading="lazy" />
              </div>
            </div>

            <div className="ms-cases" data-reveal>
              <h3>Other use cases</h3>
              <ul>
                {otherCases.map((c) => (
                  <li key={c.title}>
                    <span className="ms-case-icon"><Icon name={c.icon} /></span>
                    <span>{c.title}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ============================= INDUSTRIES ========================== */}
        <section className="ms-section">
          <div className="ms-container">
            <div className="ms-head ms-head--row" data-reveal>
              <h2>Industries we serve</h2>
              {/* <Link href="/industries" className="ms-link">Explore all industries <span aria-hidden="true">→</span></Link> */}
            </div>

            <ul className="ms-industries">
              {industries.map((x, i) => (
                <li key={x.title} data-reveal style={delay(i, 70)}>
                  <span className="ms-industry-icon"><Icon name={x.icon} /></span>
                  <span>{x.title}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ============================ FINAL CTA ============================ */}
        <section className="ms-cta" id="contact">
          <img className="ms-cta-bg" src={imgCta} alt="" aria-hidden="true" />
          <div className="ms-cta-copy">
            <span className="ms-cta-eyebrow" data-reveal>READY FOR RELIABLE, SECURE AND HIGH-PERFORMING SYSTEMS?</span>
            <h2 data-reveal style={delay(1, 120)}>Let&apos;s discuss your managed services needs.</h2>
            <div className="ms-actions" data-reveal style={delay(2, 120)}>
              <Link href="/tellusyourproblem#tell-details" className="ms-btn ms-btn-primary">
                Talk to Our Experts <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}